import { type BrandName } from './brandNames';
import { type CountryKey } from './countries';

/**
 * Where each marque is from.
 *
 * **Exhaustive on purpose**, and the one place in this folder that is. A make
 * with no logo file is an ordinary state (`brandLogos` is partial and says
 * why), because the site can draw a wordmark instead and nothing downstream
 * breaks. A make with no country is not: the brand menu's filter rail
 * partitions the makes by country and prints a count beside each one, so a
 * make missing from this record would be a make that exists under "All" and
 * nowhere else — invisible to anyone who touched a filter, and invisible in
 * a way nobody would notice until a customer did. `Record<BrandName, …>`
 * turns that into a type error the moment a name is added to
 * `brandNames.ts`.
 *
 * ## It is the marque's origin, not its owner and not its factory
 *
 * Several of these are owned elsewhere now, and a few are built elsewhere
 * too. Volvo is Chinese-owned (Geely, since 2010) and stays Swedish; Jaguar
 * and Land Rover are Indian-owned (Tata) and stay British; Bentley,
 * Lamborghini and Porsche are all inside the Volkswagen Group and are listed
 * as German, Italian and German respectively. Rolls-Royce Motor Cars is
 * BMW-owned and stays British.
 *
 * That is the right answer because of who reads it. A visitor filtering the
 * brand menu to "Germany" is asking for German engineering, and a visitor
 * filtering to "Italy" wants the Italian ones — handing them a corporate
 * ownership chart instead would put Lamborghini under Germany and Volvo
 * under China, which is accurate and useless. Where the car is *bought* is a
 * separate question with a separate answer, in `sourceMarkets` on the
 * import-a-car page.
 *
 * ## The two that are not cars
 *
 * Caterpillar (American) and Massey Ferguson are plant and agricultural
 * machinery rather than marques, and Massey Ferguson is the awkward one: the
 * company is Canadian by birth, British by the Ferguson half of its name and
 * its Coventry works, and American-owned today (AGCO). It is filed under the
 * United Kingdom because that is where the tractors Ugandan buyers know were
 * built and badged.
 */
export const brandOrigins: Record<BrandName, CountryKey> = {
  // Japan
  Toyota: 'japan',
  Mazda: 'japan',
  Lexus: 'japan',
  Subaru: 'japan',
  Isuzu: 'japan',
  Mitsubishi: 'japan',
  Nissan: 'japan',
  Honda: 'japan',
  Suzuki: 'japan',
  // Germany
  'Mercedes-Benz': 'germany',
  BMW: 'germany',
  Audi: 'germany',
  Volkswagen: 'germany',
  Porsche: 'germany',
  Maybach: 'germany',
  // United States
  Jeep: 'united-states',
  Ford: 'united-states',
  Chevrolet: 'united-states',
  Tesla: 'united-states',
  GMC: 'united-states',
  Dodge: 'united-states',
  Chrysler: 'united-states',
  Cadillac: 'united-states',
  Hummer: 'united-states',
  Caterpillar: 'united-states',
  // United Kingdom
  Jaguar: 'united-kingdom',
  'Land Rover': 'united-kingdom',
  Bentley: 'united-kingdom',
  'Rolls-Royce': 'united-kingdom',
  'Massey Ferguson': 'united-kingdom',
  // South Korea
  Hyundai: 'south-korea',
  Kia: 'south-korea',
  // China
  BYD: 'china',
  Jetour: 'china',
  Sinotruk: 'china',
  // Italy
  Ferrari: 'italy',
  Lamborghini: 'italy',
  Maserati: 'italy',
  // France
  Peugeot: 'france',
  Renault: 'france',
  // Sweden
  Volvo: 'sweden',
};
